import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import { Input, Modal } from 'components/ui';
import { b } from 'utils/i18nUtils';

import { useDispatch, useSelector } from 'hooks';
import { login } from 'modules/auth/thunk';
import { setLoginData } from 'modules/auth/actions';
import {
  getLoginData,
  hasInvalidCodeError,
  isCodeFieldDisabled,
  getCodeRetryExpiration,
} from 'modules/auth/selectors';
import { isLoadingLogin } from 'modules/selfServices/selectors';
import useCountdown from 'hooks/useCountdown';
import useQrCodeInput from 'hooks/useQrCodeInput';
import useQrCodeError from 'hooks/useQrCodeError';

import KioskModalActions from './KioskModalActions';
import useEnterSubmit from '../../hooks/useEnterSubmit';
import { QR_CODE_LENGTH } from 'modules/auth/constants';
import { LoginModals as EmailCodeModalProps } from './types';
import { SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';


const EmailCodeModal: React.FC<EmailCodeModalProps> = ({ onClose, onError, onSuccess }) => {
  const dispatch = useDispatch();
  const { state: pageState } = useLocation();

  const loginData = useSelector(getLoginData);
  const isLoading = useSelector(isLoadingLogin);
  const hasQrCodeError = useSelector(hasInvalidCodeError);
  const retryExpiration = useSelector(getCodeRetryExpiration);

  const { qrCode = '' } = loginData;

  const { countdown } = useCountdown({ date: retryExpiration });
  const codeInputDisabled = useSelector(isCodeFieldDisabled) && countdown > 0;

  const { handleChange, codeError } = useQrCodeInput(
    (value) => dispatch(setLoginData({ ...loginData, qrCode: value })),
  );
  const codeErrorMessage = useQrCodeError({ isDisabled: codeInputDisabled, hasCodeError: hasQrCodeError, codeError, countdown });

  const canSubmit = Boolean(qrCode && !codeInputDisabled && !codeError);

  const handleSubmit = async () => {
    try {
      await dispatch(login({
        qrCode,
        codeInputDisabled,
        type: pageState?.type,
      })).unwrap();
      onSuccess(SelfOfficeLoginMethod.CODE);
    } catch (error) {
      onError(error);
    }
  };

  const handleKeyDown = useEnterSubmit(handleSubmit, canSubmit);

  return (
    <Modal
      open
      onClose={onClose}
      className="max-w-modal !min-w-[50%]"
      title={
        <div role="heading" aria-level={2} data-testid="email-code-modal-title" className="text-icon font-bold">
          <FormattedMessage id="login.emailCodeModal.title" defaultMessage="Email Code" />
        </div>
      }
    >
      <div className="flex flex-col gap-9">
        <div className="text-center text-s">
          <FormattedMessage
            id="login.emailCodeModal.label"
            defaultMessage="Enter the <b>code</b> received by email"
            values={{ b }}
            tagName="span"
          />
        </div>
        <Input
          center
          value={qrCode}
          type="password"
          inputMode="numeric"
          error={codeErrorMessage}
          onChange={handleChange}
          data-testid="code-input"
          onKeyDown={handleKeyDown}
          maxLength={QR_CODE_LENGTH}
          labelClassName="text-center"
          label={<FormattedMessage id="login.code.label" defaultMessage="Code" />}
        />
        <KioskModalActions
          onClose={onClose}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          disabled={!canSubmit}
        />
      </div>
    </Modal>
  );
};

export default EmailCodeModal;
